<script lang="ts">
  import * as H from "../settings/hints.ts";
  import SignIn from "./SignIn.svelte";
  import ProfileForm from "../components/ProfileForm.svelte";
  import { app } from "../lib/store.svelte.ts";
  import { groupPath, link, router } from "../lib/router.svelte.ts";
  import { api, NetworkError } from "../lib/api.ts";
  import { passkeyError, registerPasskey } from "../lib/auth.ts";
  import { shortDateTime } from "../lib/format.ts";
  import { APP_BUILD, APP_VERSION, versionWatch } from "../lib/version.svelte.ts";
  import { downloadCsv, downloadJson } from "../lib/export.ts";
  import { nav } from "../lib/nav.svelte.ts";
  import { member } from "../lib/member.svelte.ts";
  import YourColor from "../components/YourColor.svelte";

  async function signOut() {
    await app.signOut();
    member.clear();
    router.go("/");
  }

  let downloading = $state(false);
  async function download(kind: "json" | "csv") {
    downloading = true;
    try {
      if (kind === "json") await downloadJson();
      else await downloadCsv();
    } catch (e) {
      app.toast((e as Error).message, "error");
    } finally {
      downloading = false;
    }
  }

  // ---- groups
  let newGroup = $state("");
  async function createGroup(e: SubmitEvent) {
    e.preventDefault();
    try {
      await app.createGroup(newGroup.trim(), true);
      newGroup = "";
      if (app.groupId) router.go(groupPath(app.groupId, "settings"));
    } catch (err) {
      app.toast(err instanceof NetworkError ? "Creating a group needs a connection." : (err as Error).message, "error");
    }
  }

  // ---- account
  let tokens = $state<{ label: string; scopes: string; created_at: string; last_used_at: string | null; ref: string }[]>([]);
  let newToken = $state<string | null>(null);
  let tokenLabel = $state("");
  let tokenWrite = $state(false);
  async function loadTokens() {
    try {
      tokens = (await api<{ tokens: typeof tokens }>("/api/tokens")).tokens;
    } catch {
      /* offline or signed out */
    }
  }
  $effect(() => {
    if (app.signedIn && app.role === "owner") void loadTokens();
  });
  async function createToken(e: SubmitEvent) {
    e.preventDefault();
    try {
      const r = await api<{ token: string }>("/api/tokens", { body: { label: tokenLabel.trim(), scopes: tokenWrite ? ["read", "write"] : ["read"] } });
      newToken = r.token;
      tokenLabel = "";
      await loadTokens();
    } catch (err) {
      app.toast((err as Error).message, "error");
    }
  }
  async function revoke(ref: string) {
    await api(`/api/tokens/${ref}`, { method: "DELETE", body: {} }).catch((e) => app.toast((e as Error).message, "error"));
    await loadTokens();
  }
  async function addPasskey() {
    try {
      await registerPasskey();
      app.toast("Passkey added");
    } catch (e) {
      app.toast(passkeyError(e), "error");
    }
  }
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>Your settings</h1>
      <p class="lede">Your account, devices and groups.{nav.isOwner ? " Each group's people and presets are in that group's settings." : ""}</p>
    </div>
  </div>

  {#if app.signedIn === false}
    <section class="sec"><SignIn inline /></section>
  {/if}

  {#if nav.isOwner}
  <section class="sec">
    <h2>Member view</h2>
    {#if H.YOU_MEMBER_VIEW_NOTE.trim()}<p class="note muted">{H.YOU_MEMBER_VIEW_NOTE}</p>{/if}
    <div class="inline">
      <button class="btn" onclick={() => { app.setViewAsMember(true); router.go("/"); }}>Switch to member view</button>
    </div>
  </section>
  {/if}

  <section class="sec" id="groups">
    <h2>Groups</h2>
    <div class="list">
      {#each nav.groups as g (g.id)}
        <div class="srow grp">
          <strong>{g.name}</strong>
          <a class="btn small quiet" href={groupPath(g.id)} use:link>Open</a>
          {#if nav.isOwner}<a class="btn small quiet" href={groupPath(g.id, "settings")} use:link>Settings</a>{/if}
        </div>
      {:else}
        <div class="empty">No groups yet.</div>
      {/each}
    </div>
    {#if nav.isOwner}
      <form class="inline" onsubmit={createGroup}>
        <label class="field grow"><span>New group</span><input class="input" bind:value={newGroup} placeholder="Ski trip" /></label>
        <button class="btn" disabled={!newGroup.trim()}>Create group</button>
      </form>
    {:else}
      {#if H.YOU_JOIN_NOTE.trim()}<p class="note muted">{H.YOU_JOIN_NOTE}</p>{/if}
    {/if}
  </section>

  {#if nav.asMember && !nav.preview}
    <section class="sec">
      <h2>Your color</h2>
      {#if H.YOU_COLOR_NOTE.trim()}<p class="note muted">{H.YOU_COLOR_NOTE}</p>{/if}
      <YourColor />
    </section>
  {/if}

  {#if nav.isOwner}
  <section class="sec">
    <h2>This device</h2>
    <p>
      {app.pendingCount ? `${app.pendingCount} changes waiting to sync.` : "Everything on this device is synced."}
      {#if app.lastSyncedAt}Last synced {shortDateTime(app.lastSyncedAt)}.{/if}
    </p>
    <div class="inline"><button class="btn small" onclick={() => app.syncNow()}>Sync now</button></div>
  </section>
  {/if}

  <section class="sec">
    <h2>App version</h2>
    <p>
      Split Ledger <strong>{APP_VERSION}</strong> <span class="muted">({APP_BUILD})</span>.
      {#if versionWatch.available}
        A newer version ({versionWatch.available}) is live.
      {:else if versionWatch.lastChecked}
        Up to date as of {shortDateTime(versionWatch.lastChecked)}.
      {/if}
    </p>
    {#if H.YOU_UPDATES_NOTE.trim()}<p class="note muted">{H.YOU_UPDATES_NOTE}</p>{/if}
    <div class="inline">
      {#if versionWatch.available}
        <button class="btn small primary" onclick={() => location.reload()}>Reload to update</button>
      {:else}
        <button class="btn small" disabled={versionWatch.checking} onclick={() => versionWatch.check()}>Check for updates</button>
      {/if}
      <a class="btn small quiet" href="/roadmap" use:link>What's new</a>
    </div>
  </section>

  {#if nav.isOwner}
    <section class="sec">
      <h2>Download your data</h2>
      {#if H.YOU_BACKUP_NOTE.trim()}<p class="note muted">{H.YOU_BACKUP_NOTE}</p>{/if}
      <div class="inline">
        <button class="btn small" disabled={downloading} onclick={() => download("json")}>Download everything (JSON)</button>
        <button class="btn small" disabled={downloading} onclick={() => download("csv")}>Items as a spreadsheet (CSV)</button>
      </div>
    </section>
  {/if}

  {#if app.signedIn}
    <section class="sec">
      <h2>Account</h2>
      <p>Signed in as {app.userName}.</p>
      <ProfileForm />
      <div class="inline">
        <button class="btn small" onclick={addPasskey}>Add a passkey for another device</button>
        <button class="btn small quiet" onclick={signOut}>Sign out</button>
      </div>

      {#if nav.isOwner}
        <h3 id="offsite">Off-site access</h3>
        {#if H.YOU_TOKEN_NOTE.trim()}<p class="note muted">{H.YOU_TOKEN_NOTE}</p>{/if}
        <ul class="ways">
          <li><strong>Discord:</strong> type <span class="code">/link</span> and paste the token. Then <span class="code">/owe</span>, <span class="code">/add 12.99 croissants for Emily, Chloe</span>, <span class="code">/paid Emily 20 via Zelle</span>; changes show a Confirm button. Replies are only visible to you.</li>
          <li><strong>Terminal:</strong> <span class="code">SPLIT_TOKEN=… node scripts/split.mjs owe</span>, or run it with no words for a prompt. <span class="code">ask "…"</span> uses LM Studio on that computer.</li>
          <li><strong>Text messages:</strong> coming later, with the same commands.</li>
        </ul>
        {#if newToken}
          <p class="token">
            Copy this token now; it won't be shown again: <span class="code">{newToken}</span>
            <button class="btn small" onclick={() => navigator.clipboard.writeText(newToken ?? "").then(() => app.toast("Copied"))}>Copy</button>
          </p>
        {/if}
        {#if tokens.length}
          <div class="list">
            {#each tokens as t (t.ref)}
              <div class="srow">
                <strong>{t.label}</strong>
                <span class="muted">{JSON.parse(t.scopes).includes("write") ? "Can make changes" : "Read only"}</span>
                <span class="muted">{t.last_used_at ? `used ${shortDateTime(t.last_used_at)}` : "never used"}</span>
                <button class="btn small quiet danger" onclick={() => revoke(t.ref)}>Revoke</button>
              </div>
            {/each}
          </div>
        {/if}
        <form class="inline" onsubmit={createToken}>
          <label class="field grow"><span>Token name</span><input class="input" bind:value={tokenLabel} placeholder="Discord, Laptop terminal, Nightly backup" /></label>
          <label class="check"><input type="checkbox" bind:checked={tokenWrite} /> Can make changes</label>
          <button class="btn" disabled={!tokenLabel.trim()}>Make a token</button>
        </form>
      {/if}
    </section>
  {/if}
</div>

<style>
  .ways {
    margin: 0 0 12px;
    padding-left: 18px;
    font-size: var(--t-sm);
    display: grid;
    gap: 6px;
  }



  .sec {
    display: grid;
    gap: 12px;
    padding: 22px 0;
    border-bottom: 1px solid var(--rule);
  }

  .sec h3 {
    margin-top: 10px;
    font-size: var(--t-md);
  }

  .inline {
    display: flex;
    gap: 10px;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .grow {
    flex: 1 1 220px;
  }

  .note {
    font-size: var(--t-sm);
    max-width: 70ch;
  }

  .srow {
    display: grid;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: var(--surface);
  }

  .srow {
    grid-template-columns: 1fr 80px 1fr auto;
  }

  .srow.grp {
    grid-template-columns: 1fr auto auto;
  }

  .token {
    padding: 10px 12px;
    border-radius: var(--r-md);
    background: var(--wash);
    word-break: break-all;
  }

  .check {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    min-height: 42px;
    font-size: var(--t-sm);
  }

  @media (max-width: 640px) {

    .srow {
      grid-template-columns: 1fr auto;
    }
  }
</style>
